import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../components/supabase/config.js';

// Converts "YYYY-MM-DDTHH:MM" -> "MM/DD/YYYY HH:MM"
function formatMMDDYYYY_HHMM(datetimeLocalValue) {
  if (!datetimeLocalValue) return '';

  const [datePart, timePart] = datetimeLocalValue.split('T');
  if (!datePart || !timePart) return '';

  const [yyyy, mm, dd] = datePart.split('-');
  const [HH, MM] = timePart.split(':');

  if (!yyyy || !mm || !dd || HH === undefined || MM === undefined) return '';

  return `${mm}/${dd}/${yyyy} ${HH}:${MM}`;
}

const TrackingTable = () => {
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [allTrackingNumbers, setAllTrackingNumbers] = useState([]);
  const [newTrackingNumber, setNewTrackingNumber] = useState('');

  // store formatted value for DB insert: "MM/DD/YYYY HH:MM"
  const [commonDate, setCommonDate] = useState('');
  // store raw value for input: "YYYY-MM-DDTHH:MM"
  const [commonDateRaw, setCommonDateRaw] = useState('');

  // Status dropdown selected value (default blank)
  const [commonStatus, setCommonStatus] = useState('');

  const [successMessage, setSuccessMessage] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  // Status dropdown options
  const statusOptions = [
    'Bangkok Warehouse',
    'Loaded',
    'Loading',
    'Sailing',
    'Clearance',
    'delivered',
    'arrived',
    'Manila Warehouse',
    'berthing',
    'out for delivery',
    'not found',
    'no tracking',
    'sailing near Phil.SEA',
    'Manila port',
    'Sorting of Boxes',
  ];

  // Fetch all tracking numbers from the database for dropdown suggestions
  useEffect(() => {
    const fetchTrackingNumbers = async () => {
      const { data, error } = await supabase
        .from('trackingfbt_audit')
        .select('tracking_id');

      if (error) {
        console.error('Error fetching tracking numbers:', error);
      } else {
        // de-dupe
        const unique = Array.from(new Set(data.map((item) => item.tracking_id)));
        setAllTrackingNumbers(unique);
      }
    };
    fetchTrackingNumbers();
  }, []);

  // Filter suggestions based on input
  const handleInputChange = (e) => {
    const userInput = e.target.value;
    setNewTrackingNumber(userInput);

    const filtered = allTrackingNumbers.filter((trackingNumber) =>
      trackingNumber.toLowerCase().includes(userInput.toLowerCase())
    );
    setFilteredSuggestions(filtered);
  };

  // Add a new tracking number to the list
  const addTrackingNumber = (trackingNumber) => {
    const trimmed = (trackingNumber || '').trim();
    if (!trimmed) return;

    if (!trackingNumbers.find((item) => item.trackingNumber === trimmed)) {
      setTrackingNumbers((prev) => [...prev, { trackingNumber: trimmed }]);
      setNewTrackingNumber('');
      setFilteredSuggestions([]);
    }
  };

  // Handle selection from dropdown
  const handleSuggestionClick = (suggestion) => {
    addTrackingNumber(suggestion);
  };

  // Remove a tracking number from the list
  const removeTrackingNumber = (index) => {
    setTrackingNumbers((prev) => prev.filter((_, i) => i !== index));
  };

  // Common Date change (datetime-local -> formatted string)
  const handleCommonDateChange = (e) => {
    const raw = e.target.value; // "YYYY-MM-DDTHH:MM"
    setCommonDateRaw(raw);

    const formatted = formatMMDDYYYY_HHMM(raw); // "MM/DD/YYYY HH:MM"
    setCommonDate(formatted);
  };

  // Handle submission to Supabase
  const handleSubmit = async () => {
    if (trackingNumbers.length === 0) {
      console.error('No tracking numbers to submit.');
      return;
    }
    if (!commonDate) {
      console.error('Common date is required.');
      return;
    }

    // Popup validation for blank status
    if (!commonStatus) {
      window.alert('Please choose the status');
      return;
    }

    const { data: maxAuditIdData, error: fetchError } = await supabase
      .from('trackingfbt_audit')
      .select('audit_id')
      .order('audit_id', { ascending: false })
      .limit(1);

    if (fetchError) {
      console.error('Error fetching max audit_id:', fetchError);
      return;
    }

    let nextAuditId = maxAuditIdData.length > 0 ? maxAuditIdData[0].audit_id + 1 : 1;

    const dataToInsert = trackingNumbers.map((item) => ({
      tracking_id: item.trackingNumber,
      audit_id: nextAuditId++,
      date_loaded: commonDate,      // "MM/DD/YYYY HH:MM"
      remarks: commonStatus,        // stored in DB as remarks
    }));

    const { error } = await supabase.from('trackingfbt_audit').insert(dataToInsert).select();

    if (error) {
      console.error('Error inserting data:', error);
    } else {
      setSuccessMessage('All entries have been submitted to the database!');
      setTrackingNumbers([]);
      setCommonDate('');
      setCommonDateRaw('');
      setCommonStatus(''); // reset to blank
    }
  };

  return (
    <div className="container">
      <h2>Tracking Table</h2>

      {/* Input for adding tracking numbers */}
      <div className="mb-3">
        <input
          type="text"
          value={newTrackingNumber}
          placeholder="Enter or search tracking number"
          onChange={handleInputChange}
          className="form-control mb-2"
        />
        <button onClick={() => addTrackingNumber(newTrackingNumber)} className="btn btn-primary">
          Add Tracking Number
        </button>

        {/* Dropdown Suggestions */}
        {filteredSuggestions.length > 0 && (
          <ul className="list-group mt-2">
            {filteredSuggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="list-group-item list-group-item-action"
                style={{ cursor: 'pointer' }}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Display list of tracking numbers */}
      <table className="table">
        <thead>
          <tr>
            <th>Tracking Number</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {trackingNumbers.map((item, index) => (
            <tr key={index}>
              <td>{item.trackingNumber}</td>
              <td>
                <button onClick={() => removeTrackingNumber(index)} className="btn btn-danger btn-sm">
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Common Date and Status Inputs */}
      <div className="mb-3">
        <label>Date (MM/DD/YYYY HH:MM)</label>
        <input
          type="datetime-local"
          value={commonDateRaw}
          onChange={handleCommonDateChange}
          className="form-control"
        />
        {commonDate && (
          <div className="form-text">
            Saved format: <strong>{commonDate}</strong>
          </div>
        )}
      </div>

      {/* Status dropdown (default blank) */}
      <div className="mb-3">
        <label>Status</label>
        <select
          value={commonStatus}
          onChange={(e) => setCommonStatus(e.target.value)}
          className="form-select"
        >
          <option value="">-- Select status --</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {/* Submit Button */}
      <button onClick={handleSubmit} className="btn btn-success">
        Submit to Database
      </button>

      {/* Success Message */}
      {successMessage && <p className="text-success mt-3">{successMessage}</p>}
    </div>
  );
};

export default TrackingTable;
